import { cn } from '@/lib/cn';

/**
 * QuizProgress — five small dots + a "N/M" counter (quiz spec §4.5).
 *
 * Dot states match the spec:
 *   - Unanswered: `#EFE5D2`, 8px diameter.
 *   - Correct:    `#4CAF50`, 10px, slight pop (visible scale difference).
 *   - Incorrect:  `#D94F3A`, 10px.
 *   - Current:    `#EFE5D2` with a `#2E8A8A` ring.
 *
 * We derive the per-dot state from the parent's `perQuestion` summary
 * rather than tracking it here, so the current index stays in sync
 * with the question card.
 */
export interface PerQuestionSummary {
  questionId: string;
  picked: string | null;
  isCorrect: boolean | null;
}

export interface QuizProgressProps {
  total: number;
  /** 0-based index of the question currently being answered. */
  current: number;
  perQuestion: PerQuestionSummary[];
}

export function QuizProgress({ total, current, perQuestion }: QuizProgressProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center" style={{ gap: '6px' }}>
        {Array.from({ length: total }).map((_, i) => {
          const summary = perQuestion[i];
          const answered = summary?.picked != null;
          const isCurrent = i === current;
          const isCorrect = summary?.isCorrect === true;
          const isWrong = summary?.isCorrect === false;

          const size = answered ? 10 : 8;
          const bg = isCorrect ? '#4CAF50' : isWrong ? '#D94F3A' : '#EFE5D2';

          return (
            <span
              key={i}
              aria-label={
                isCurrent
                  ? `Question ${i + 1} of ${total} — current`
                  : answered
                    ? `Question ${i + 1} of ${total} — ${isCorrect ? 'correct' : 'wrong'}`
                    : `Question ${i + 1} of ${total}`
              }
              className={cn(
                'inline-block rounded-full transition-[box-shadow,width,height,background-color]',
              )}
              style={{
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: bg,
                boxShadow: isCurrent ? '0 0 0 2px #2E8A8A' : 'none',
              }}
            />
          );
        })}
      </div>
      <span
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '13px',
          color: '#8A7F72',
        }}
      >
        {Math.min(current + 1, total)}/{total}
      </span>
    </div>
  );
}
